import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  EyeIcon,
  UserGroupIcon,
  InboxIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { websiteApi } from '../api/website.api';

interface AnalyticsPanelProps {
  websiteId: string;
}

type DatePreset = '7d' | '30d' | '90d' | 'all';

function toISODate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function presetToRange(preset: DatePreset): { fromDate?: string; toDate?: string } {
  if (preset === 'all') return {};
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { fromDate: toISODate(from), toDate: toISODate(to) };
}

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  accent: string;
  hint?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, accent, hint }) => (
  <div className="nova-card p-4 sm:p-5 flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${accent}1A`, color: accent }}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-nova-text leading-tight">{value.toLocaleString()}</p>
      <p className="text-sm text-nova-text2">{label}</p>
      {hint && <p className="text-xs text-nova-text3 mt-0.5">{hint}</p>}
    </div>
  </div>
);

const PRESETS: { id: DatePreset; label: string }[] = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

const AnalyticsPanel: React.FC<AnalyticsPanelProps> = ({ websiteId }) => {
  const [preset, setPreset] = useState<DatePreset>('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [useCustom, setUseCustom] = useState(false);

  const dateRange = useMemo(() => {
    if (useCustom && (customFrom || customTo)) {
      return { fromDate: customFrom || undefined, toDate: customTo || undefined };
    }
    return presetToRange(preset);
  }, [preset, useCustom, customFrom, customTo]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics', websiteId, dateRange.fromDate, dateRange.toDate],
    queryFn: () => websiteApi.getAnalytics(websiteId, dateRange),
    enabled: !!websiteId,
  });

  return (
    <div className="space-y-6">
      <div className="nova-card p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-nova-text2 mr-2">Date range:</span>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setPreset(p.id);
                setUseCustom(false);
              }}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                !useCustom && preset === p.id
                  ? 'bg-nova-sky text-white border-nova-sky'
                  : 'border-nova-border text-nova-text2 hover:bg-nova-bg2'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <input
              type="date"
              value={customFrom}
              onChange={(e) => {
                setCustomFrom(e.target.value);
                setUseCustom(true);
              }}
              className="px-2 py-1.5 text-sm border border-nova-border rounded-lg"
            />
            <span className="text-nova-text3 text-sm">to</span>
            <input
              type="date"
              value={customTo}
              onChange={(e) => {
                setCustomTo(e.target.value);
                setUseCustom(true);
              }}
              className="px-2 py-1.5 text-sm border border-nova-border rounded-lg"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
        </div>
      ) : isError || !data ? (
        <div className="nova-card p-6 text-center text-nova-text3">Could not load analytics.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Website Views" value={data.totalViews} icon={EyeIcon} accent="#2563EB" hint="In selected date range" />
          <StatCard label="Unique Visitors" value={data.uniqueVisitors} icon={UserGroupIcon} accent="#059669" hint="In selected date range" />
          <StatCard
            label="Enquiries"
            value={data.totalEnquiries}
            icon={InboxIcon}
            accent="#DC2626"
            hint={`${data.unreadEnquiries} unread (all time)`}
          />
          <StatCard label="Newsletter Subscribers" value={data.newsletterSubscribers} icon={EnvelopeIcon} accent="#7C3AED" hint="In selected date range" />
          <StatCard label="Sections" value={data.sectionCount} icon={Squares2X2Icon} accent="#EA580C" hint="Current total" />
          <StatCard label="Products" value={data.productCount} icon={ShoppingBagIcon} accent="#0891B2" hint="Current total" />
        </div>
      )}
    </div>
  );
};

export default AnalyticsPanel;
